import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { track } from "@/lib/analytics";

const STORAGE_KEY = "truth_or_debt_cookie_consent";

/**
 * Apply consent to Google Consent Mode (if gtag exists) and to dataLayer.
 * Safe no-op if GTM not installed yet — ready for future GTM/GA integration.
 */
function applyConsentToGtag(consent) {
  try {
    if (typeof window === "undefined") return;
    // Push to dataLayer so GTM can read it
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "consent_update",
      consent,
    });
    // Google Consent Mode v2
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        ad_storage: consent.marketing ? "granted" : "denied",
        ad_user_data: consent.marketing ? "granted" : "denied",
        ad_personalization: consent.marketing ? "granted" : "denied",
        analytics_storage: consent.analytics ? "granted" : "denied",
        security_storage: "granted",
      });
    }
  } catch (_) {
    /* silent */
  }
}

function loadConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.warn("CookieConsent: read failed", err?.message);
    return null;
  }
}

function saveConsent(consent) {
  const payload = { ...consent, version: 1, ts: Date.now() };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (err) {
    console.warn("CookieConsent: persist failed", err?.message);
  }
  applyConsentToGtag(payload);
  // Notify other components (e.g. TrackingLoader) so they can inject pixels
  try {
    window.dispatchEvent(new CustomEvent("cookie-consent-update", { detail: payload }));
  } catch (err) {
    console.warn("CookieConsent: dispatch failed", err?.message);
  }
  return payload;
}

export default function CookieConsent({ privacyHref = "/privacy" }) {
  const [show, setShow] = useState(false);
  const [openPrefs, setOpenPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  // Decide whether to show the banner on mount (after 1s for hero visibility)
  useEffect(() => {
    const existing = loadConsent();
    if (existing) {
      // Re-apply to gtag in case page reload
      applyConsentToGtag(existing);
      return;
    }
    // Initial default: all denied (essential always on)
    applyConsentToGtag({ essential: true, analytics: false, marketing: false });
    const t = setTimeout(() => setShow(true), 1000);
    return () => clearTimeout(t);
  }, []);

  const acceptAll = () => {
    saveConsent({ essential: true, analytics: true, marketing: true });
    track("cookie_consent_accept_all", {
      campaignId: window.__currentCampaignId,
      payload: { analytics: true, marketing: true },
    });
    setShow(false);
  };

  const savePrefs = () => {
    saveConsent({ essential: true, analytics, marketing });
    track("cookie_consent_custom_save", {
      campaignId: window.__currentCampaignId,
      payload: { analytics, marketing },
    });
    setOpenPrefs(false);
    setShow(false);
  };

  const openPreferences = () => {
    const existing = loadConsent();
    if (existing) {
      setAnalytics(!!existing.analytics);
      setMarketing(!!existing.marketing);
    }
    setOpenPrefs(true);
  };

  // Expose a global function to re-open the preferences from anywhere (e.g. footer "Cookie settings" link)
  useEffect(() => {
    window.openCookiePreferences = () => {
      openPreferences();
      setShow(false);
    };
    // Hide banner when consent is auto-granted (e.g. after form submit)
    const onAutoGrant = (e) => {
      if (e?.detail?.auto_granted) {
        setShow(false);
        setOpenPrefs(false);
      }
    };
    window.addEventListener("cookie-consent-update", onAutoGrant);
    return () => {
      delete window.openCookiePreferences;
      window.removeEventListener("cookie-consent-update", onAutoGrant);
    };
  }, []);

  return (
    <>
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed bottom-4 left-4 right-4 z-[60] flex justify-center pointer-events-none"
            data-testid="cookie-banner"
            dir="rtl"
          >
            <div className="pointer-events-auto w-full max-w-3xl bg-[#292524] text-stone-100 rounded-2xl shadow-[0_24px_60px_rgba(0,0,0,0.5)] p-5 md:p-6 border border-white/10">
              <div className="flex items-start gap-3 md:gap-4 flex-col md:flex-row">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl" aria-hidden>🚀</span>
                    <h3 className="font-[800] text-base md:text-lg">המסלול שלכם, ההזדמנויות שלכם.</h3>
                  </div>
                  <p className="text-sm text-stone-300 leading-[1.6]">
                    כדי שנוכל לתת לכם את <strong className="text-white">הכלים המדויקים</strong> שיוציאו אתכם מהמינוס מהר יותר, אנחנו משתמשים ב"עוגיות" טכנולוגיות. במקום להציג לכם תוכן כללי, אישור מלא מאפשר לנו ללמוד מה באמת מעניין אתכם, ולהגיש לכם את הפתרונות, המדריכים וההזדמנויות ש<strong className="text-white">יחסכו לכם הכי הרבה כסף וזמן</strong>.{" "}
                    <a href={privacyHref} className="underline hover:text-white" data-testid="cookie-banner-privacy-link">
                      למדיניות הפרטיות המלאה
                    </a>
                    .
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row md:flex-col gap-2 w-full md:w-auto md:min-w-[230px]">
                  <Button
                    onClick={acceptAll}
                    data-testid="cookie-accept-all-btn"
                    className="h-12 rounded-full bg-brand-amber hover:bg-brand-amberHover text-white font-bold text-sm md:text-[15px] px-5"
                  >
                    ✅ מעולה, תנו לי את החוויה המלאה!
                  </Button>
                  <Button
                    onClick={openPreferences}
                    variant="outline"
                    data-testid="cookie-preferences-btn"
                    className="h-11 rounded-full bg-transparent border-white/30 text-stone-200 hover:bg-white/10 hover:text-white font-bold text-sm"
                  >
                    ⚙️ אני מעדיף/ה להגדיר בעצמי
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={openPrefs} onOpenChange={setOpenPrefs}>
        <DialogContent className="max-w-lg" dir="rtl" data-testid="cookie-preferences-dialog">
          <DialogHeader>
            <DialogTitle className="font-[800] text-2xl flex items-center gap-2">
              <Cookie className="w-6 h-6 text-brand-amber" />
              הגדרות עוגיות
            </DialogTitle>
            <DialogDescription className="text-stone-500 leading-[1.7]">
              בחרו אילו סוגי עוגיות תרצו לאפשר. אפשר לשנות בכל עת.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 mt-2">
            <ToggleRow
              testId="toggle-essential"
              title="המנוע של האתר (חובה)"
              text="ההגדרות הבסיסיות שמאפשרות למחשבונים שלנו לעבוד בצורה חלקה ולשמור על אבטחת הנתונים שלכם בזמן הגלישה."
              value={true}
              locked
            />
            <ToggleRow
              testId="toggle-marketing"
              title="הזדמנויות כלכליות וקהילה"
              text="אל תפספסו הזדמנויות. הפעלת אופציה זו מאפשרת לנו להזמין אתכם לקהילות הסגורות שלנו, להציג לכם פתרונות פיננסיים ברשת, ולדאוג שלא תפספסו אף תוכן שיכול לשפר את המאזן החודשי שלכם."
              badge="מומלץ לצמיחה מקסימלית"
              value={marketing}
              onChange={setMarketing}
            />
            <ToggleRow
              testId="toggle-analytics"
              title="שיפור המדריכים"
              text="עוזר לנו לדעת (באופן אנונימי לחלוטין) אילו כלים אהבתם יותר, כדי שנוכל להמשיך לפתח מחשבונים ומדריכים שבאמת עוזרים לכם ולשאר הקהילה לנצח את השיטה."
              value={analytics}
              onChange={setAnalytics}
            />
          </div>

          <DialogFooter className="gap-2 mt-2">
            <Button
              variant="outline"
              onClick={() => setOpenPrefs(false)}
              className="rounded-full"
            >
              ביטול
            </Button>
            <Button
              onClick={savePrefs}
              data-testid="cookie-save-prefs-btn"
              className="rounded-full bg-brand-amber hover:bg-brand-amberHover text-white font-bold"
            >
              שמירת המסלול שלי
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ToggleRow({ title, text, value, onChange, locked, testId, badge }) {
  return (
    <div className="bg-stone-50 border border-stone-200 rounded-2xl p-4 relative">
      {badge && (
        <span className="absolute -top-2.5 right-4 text-[10px] font-[800] uppercase tracking-wider px-2.5 py-1 rounded-full bg-brand-amber text-white shadow-md">
          ✨ {badge}
        </span>
      )}
      <div className="flex items-start justify-between gap-3 mb-1">
        <Label className="font-[800] text-base">{title}</Label>
        {locked ? (
          <span className="text-xs font-bold px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 whitespace-nowrap">
            תמיד דלוק
          </span>
        ) : (
          <Switch
            checked={value}
            onCheckedChange={onChange}
            data-testid={testId}
            className="data-[state=checked]:bg-brand-amber"
          />
        )}
      </div>
      <p className="text-sm text-stone-600 leading-[1.6]">{text}</p>
    </div>
  );
}
